import { useCallback } from 'react'
import {
  DuplicationCheck,
  DuplicationData,
  IDuplicationCheckResult,
  OnboardingDuplicationActionType,
  OnboardingDuplicationCheckType,
  OnboardingStepKey,
  POSTAddressCheck
} from '@cbgms/api'
import * as routing from '../onboarding/constants/routes'
import {
  emailCheck,
  enrichShop,
  getVATNumberCheck,
  orderAccountCheck,
  postAddressCheck,
  postOnboardingWholesalerCredentials,
  shopEmailCheck,
  shopMGIDCheck,
  shopPhoneNumberCheck
} from 'app/auth/state/actions'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useOnboardingContext } from 'app/auth/components/onboarding/use-onboarding-context'
import { useCountryFromUrl } from '@cbgms/base/hooks'

// Hook that only posts onboarding wholesaler credentials for Netherlands
const usePostOnboardingWholesalerCredentialsForNL = () => {
  const dispatch = useDispatch()
  const { isNetherlands } = useCountryFromUrl()
  const isGAShop = useIsGAShop()

  return useCallback(
    async (params: Parameters<typeof postOnboardingWholesalerCredentials>[0], shop: IDuplicationCheckResult) => {
      if (isNetherlands) {
        const enrichDetails = {
          ShopID: shop.ID,
          CompanyUUID: shop.CompanyUUID,
          VisitorCountry: shop.VisitorCountry
        }
        isGAShop(shop) && (await dispatch(enrichShop(params.TicketUUID?.toString() || '', enrichDetails)))
        dispatch(postOnboardingWholesalerCredentials(params))
      }
    },
    [dispatch, isNetherlands, isGAShop]
  )
}

const useIsGAShop = () => {
  return useCallback((shop: IDuplicationCheckResult) => {
    const isGAShop = !shop.ModuleGMSEnabled && !shop.ModulePortalEnabled
    return isGAShop
  }, [])
}

const findActive = (duplicationChecks: DuplicationCheck[], checkType: OnboardingDuplicationCheckType): DuplicationCheck | null => {
  return duplicationChecks?.find(a => a.Active && a.CheckType === checkType) || null
}

const useNavigateToAlreadyRegistered = (basePath: string) => {
  const h = useHistory()
  return useCallback(() => {
    h.push(routing.urlAlreadyRegisteredUserEmail(basePath))
    throw new Error('email already in use')
  }, [h])
}

const useNavigateToGenericDuplicationRejection = (_: string) => {
  const h = useHistory()
  const { pathname } = useLocation()
  return useCallback(
    (dupe: DuplicationCheck) => {
      h.push(routing.urlDuplicationError(pathname, dupe.CheckType))
      throw new Error('generic duplication error')
    },
    [h]
  )
}

const useNavigateToInviteUserToOtherShop = (_: string) => {
  const h = useHistory()
  const { pathname } = useLocation()
  return useCallback(
    (dupe: DuplicationCheck) => {
      h.push(routing.urlInviteUserToOtherShop(pathname, dupe.CheckType))
      throw new Error('generic duplication error')
    },
    [h]
  )
}

const useNavigateToVATAlreadyRegisteredMessage = (basePath: string) => {
  const h = useHistory()
  return useCallback(() => {
    h.push(routing.urlAlreadyRegisteredVAT(basePath))
    throw new Error('email already in use')
  }, [h])
}

const useNavigateToMGIDAlreadyRegisteredMessage = (basePath: string) => {
  const h = useHistory()
  return useCallback(() => {
    h.push(routing.urlAlreadyRegisteredMGID(basePath))
    throw new Error('MGID already in use')
  }, [h])
}

const useNavigateToInvalidAxNumberMessage = (basePath: string) => {
  const h = useHistory()
  return useCallback(() => {
    h.push(routing.urlAlreadyRegisteredAxNumber(basePath))
    throw new Error('Invalid AxNumber')
  }, [h])
}

const useHandleDuplication = (check: DuplicationCheck | null, basePath: string): ((data: DuplicationData) => void) => {
  const handleAlreadyRegistered = useNavigateToAlreadyRegistered(basePath)
  const handleGenericDuplicationRejections = useNavigateToGenericDuplicationRejection(basePath)
  const handleDuplicateVATRejection = useNavigateToVATAlreadyRegisteredMessage(basePath)
  const handleDuplicateMGIDRejection = useNavigateToMGIDAlreadyRegisteredMessage(basePath)
  const handleInviteUserToOtherShop = useNavigateToInviteUserToOtherShop(basePath)
  const { setDuplicationData } = useOnboardingContext()

  return useCallback(
    (data: DuplicationData) => {
      if (!check?.CheckType) {
        return
      }
      setDuplicationData(data)
      switch (data.dupe.ActionOnDuplicate) {
        case OnboardingDuplicationActionType.InviteEmployeeToOtherShop:
          handleInviteUserToOtherShop(check)
          break
        case OnboardingDuplicationActionType.RejectAndShowSupportDeskMessage:
          if (check.CheckType === OnboardingDuplicationCheckType.LoginEmail) {
            handleAlreadyRegistered()
          } else if (check.CheckType === OnboardingDuplicationCheckType.ShopVat) {
            handleDuplicateVATRejection()
          } else if (check.CheckType === OnboardingDuplicationCheckType.ShopMGID) {
            handleDuplicateMGIDRejection()
          } else {
            handleGenericDuplicationRejections(check)
          }
          break
      }
    },
    [check]
  )
}

type DuplicationCheckOrderAccountHandler = (orderAccountID: string, wholesalerUUID: string) => Promise<void>
export const useDuplicateCheckOrderAccount = (
  duplicationChecks: DuplicationCheck[],
  basePath: string
): DuplicationCheckOrderAccountHandler => {
  const dispatch = useDispatch()
  const { ticketUUID } = useOnboardingContext()
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.OrderAccount)
  const handleDuplicate = useHandleDuplication(check, basePath)
  return useCallback(
    async (orderAccountID: string, wholesalerUUID: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateOrderAccount } = await dispatch(orderAccountCheck(ticketUUID, orderAccountID, wholesalerUUID))
        if (isDuplicateOrderAccount?.IsDuplicate) {
          handleDuplicate({ dupe: isDuplicateOrderAccount })
        }
      }
    },
    [check]
  )
}

type DuplicationCheckMGIDHandler = (mgId: string) => Promise<void>
export const useDuplicateCheckMGID = (duplicationChecks: DuplicationCheck[], basePath: string): DuplicationCheckMGIDHandler => {
  const dispatch = useDispatch()
  const { ticketUUID, steps } = useOnboardingContext()
  const Credentials = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.ShopMGID)
  const handleDuplicate = useHandleDuplication(check, basePath)
  const postCredentialsForNL = usePostOnboardingWholesalerCredentialsForNL()

  return useCallback(
    async (mgId: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateMGID } = await dispatch(shopMGIDCheck(ticketUUID, mgId))
        if (isDuplicateMGID?.IsDuplicate) {
          postCredentialsForNL(
            {
              TicketUUID: ticketUUID?.toString() || '',
              Credentials,
              ShopID: isDuplicateMGID.ID,
              CompanyUUID: isDuplicateMGID.CompanyUUID
            },
            isDuplicateMGID
          )
          handleDuplicate({ dupe: isDuplicateMGID })
        }
      }
    },
    [check, postCredentialsForNL]
  )
}

export const useValidateAxNumber = (basePath: string) => {
  const { steps } = useOnboardingContext()
  const handleNavigateToInvalidAxNumber = useNavigateToInvalidAxNumberMessage(basePath)
  const axNumber = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData?.AXNumber
  return useCallback(() => {
    if (!axNumber) {
      handleNavigateToInvalidAxNumber()
    }
  }, [axNumber])
}

type DuplicationCheckVATHandler = (vatNumber: string) => Promise<void>
export const useDuplicateCheckVAT = (duplicationChecks: DuplicationCheck[], basePath: string): DuplicationCheckVATHandler => {
  const dispatch = useDispatch()
  const { ticketUUID, steps } = useOnboardingContext()
  const Credentials = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.ShopVat)
  const handleDuplicate = useHandleDuplication(check, basePath)
  const postCredentialsForNL = usePostOnboardingWholesalerCredentialsForNL()

  return useCallback(
    async (vatNumber: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateVAT } = await dispatch(getVATNumberCheck(ticketUUID, vatNumber))
        if (isDuplicateVAT?.IsDuplicate) {
          postCredentialsForNL(
            {
              TicketUUID: ticketUUID?.toString() || '',
              Credentials,
              ShopID: isDuplicateVAT.ID,
              CompanyUUID: isDuplicateVAT.CompanyUUID
            },
            isDuplicateVAT
          )
          handleDuplicate({ dupe: isDuplicateVAT })
        }
      }
    },
    [check, postCredentialsForNL]
  )
}

type DuplicationCheckSignupCredentialsHandler = (loginEmail: string) => Promise<void>
export const useDuplicateCheckSignupCredentials = (
  duplicationChecks: DuplicationCheck[],
  basePath: string
): DuplicationCheckSignupCredentialsHandler => {
  const dispatch = useDispatch()
  const { ticketUUID } = useOnboardingContext()
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.LoginEmail)
  const handleDuplicate = useHandleDuplication(check, basePath)
  return useCallback(
    async (email: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateEmail } = await dispatch(emailCheck(ticketUUID, email))
        if (isDuplicateEmail?.IsDuplicate) {
          handleDuplicate({ loginEmail: email, dupe: isDuplicateEmail })
        }
      }
    },
    [check]
  )
}

type DuplicationCheckShopPhoneNumberHandler = (phoneNumber: string) => Promise<void>
export const useDuplicateCheckShopPhoneNumber = (
  duplicationChecks: DuplicationCheck[],
  basePath: string
): DuplicationCheckShopPhoneNumberHandler => {
  const dispatch = useDispatch()
  const { ticketUUID, steps } = useOnboardingContext()
  const Credentials = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.ShopPhoneNumber)
  const handleDuplicate = useHandleDuplication(check, basePath)
  const postCredentialsForNL = usePostOnboardingWholesalerCredentialsForNL()

  return useCallback(
    async (phoneNumber: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicatePhoneNumber } = await dispatch(shopPhoneNumberCheck(ticketUUID, phoneNumber))
        if (isDuplicatePhoneNumber?.IsDuplicate) {
          postCredentialsForNL(
            {
              TicketUUID: ticketUUID?.toString() || '',
              Credentials,
              ShopID: isDuplicatePhoneNumber.ID,
              CompanyUUID: isDuplicatePhoneNumber.CompanyUUID
            },
            isDuplicatePhoneNumber
          )
          handleDuplicate({ dupe: isDuplicatePhoneNumber })
        }
      }
    },
    [check, postCredentialsForNL]
  )
}

type DuplicationCheckShopAddressHandler = (address: POSTAddressCheck) => Promise<void>
export const useDuplicateCheckShopAddress = (
  duplicationChecks: DuplicationCheck[],
  basePath: string
): DuplicationCheckShopAddressHandler => {
  const dispatch = useDispatch()
  const { ticketUUID, steps } = useOnboardingContext()
  const Credentials = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.ShopAddress)
  const handleDuplicate = useHandleDuplication(check, basePath)
  const postCredentialsForNL = usePostOnboardingWholesalerCredentialsForNL()

  return useCallback(
    async (address: POSTAddressCheck) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateShopAddress } = await dispatch(postAddressCheck(ticketUUID, address))
        if (isDuplicateShopAddress?.IsDuplicate) {
          postCredentialsForNL(
            {
              TicketUUID: ticketUUID?.toString() || '',
              Credentials,
              ShopID: isDuplicateShopAddress.ID,
              CompanyUUID: isDuplicateShopAddress.CompanyUUID
            },
            isDuplicateShopAddress
          )
          handleDuplicate({ dupe: isDuplicateShopAddress })
        }
      }
    },
    [check, postCredentialsForNL]
  )
}

type DuplicationCheckShopEmailHandler = (email: string) => Promise<void>
export const useDuplicateCheckShopEmail = (duplicationChecks: DuplicationCheck[], basePath: string): DuplicationCheckShopEmailHandler => {
  const dispatch = useDispatch()
  const { ticketUUID, steps } = useOnboardingContext()
  const Credentials = steps?.find(step => step.stepData.Key === OnboardingStepKey.WholesalerCredentialsLogin)?.stepData.InitialData
  const check = findActive(duplicationChecks, OnboardingDuplicationCheckType.ShopEmail)
  const handleDuplicate = useHandleDuplication(check, basePath)
  const postCredentialsForNL = usePostOnboardingWholesalerCredentialsForNL()

  return useCallback(
    async (email: string) => {
      const shouldRun = !!check?.Active
      if (shouldRun) {
        const { Data: isDuplicateEmail } = await dispatch(shopEmailCheck(ticketUUID, email))
        if (isDuplicateEmail?.IsDuplicate) {
          postCredentialsForNL(
            {
              TicketUUID: ticketUUID?.toString() || '',
              Credentials,
              ShopID: isDuplicateEmail.ID,
              CompanyUUID: isDuplicateEmail.CompanyUUID
            },
            isDuplicateEmail
          )
          handleDuplicate({ dupe: isDuplicateEmail })
        }
      }
    },
    [check, postCredentialsForNL]
  )
}
